import React, { useState, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { StoreSelectionSheet } from './StoreSelectionSheet'
import { getStoreId, getSelectedStoreId } from '@atredux/selectors/storeSelector'
import { _navigateToTool } from '@components/SideBar/utils/SideBarUtil'
import { getAssociateEntryTool, getCurrentPlatformMode } from '@services/ToolMenuConfigService'
import { getIsLoggedIn } from '@atredux/selectors/ihmLoginTokenInfoSelector'
import { getIsLoggedIn as getIsIIBloggedIn } from '@atredux/selectors/loginTokenInfoSelector'
import { EnvironmentDetail } from '@amzn/ihm-associate-tools-js-lib'
import { NO_CURRENT_STORE } from '@src/constants'
import { useAuth } from '@services/auth/AuthContext'

/**
 * StoreSelectionSheetController
 *
 * Shows store selection sheet on app load, before side menu.
 * Sheet must be dismissed by selecting a store.
 */
export const StoreSelectionSheetController: React.FC = () => {
    const [isSheetOpen, setIsSheetOpen] = useState(false)
    const [hasCheckedInitialStore, setHasCheckedInitialStore] = useState(false)
    const [shouldNavigateToAssociateEntry, setShouldNavigateToAssociateEntry] = useState(false)
    const location = useLocation()

    const storeId = useSelector(getStoreId)
    const selectedStoreId = useSelector(getSelectedStoreId)
    const isLoggedIn = useSelector(getIsLoggedIn)
    const isIIBLoggedIn = useSelector(getIsIIBloggedIn)
    // Native (React Native) login goes through AuthContext and never sets the legacy
    // IhmLogin/IIB redux flags, so count it too.
    const { isAuthenticated } = useAuth()
    const isUserLoggedIn = isLoggedIn || isIIBLoggedIn || isAuthenticated

    // In preprod, skip login check (mock authentication)
    const isProd = EnvironmentDetail.getDomain() === 'prod'
    const shouldCheckLogin = isProd

    useEffect(() => {
        // Only check once on mount
        if (hasCheckedInitialStore) {
            console.log('JWOStoreManagementPlatform: Already checked initial store, skipping')
            return
        }

        // Skip on login/logout pages
        if (location.pathname === '/login' ||
        location.pathname === '/logout' ||
        location.pathname === '/') {
            console.log('JWOStoreManagementPlatform: On login/logout page, skipping store check')
            return
        }

        // Skip on JwoStoreManagementPortal pages
        if (location.pathname === '/web') {
            console.log('JWOStoreManagementPlatform: On JwoStoreManagementPortal, skipping store check')
            return
        }

        // Skip in cloudscape (web portal) mode — no store selection needed
        if (getCurrentPlatformMode() === 'cloudscape') {
            console.log('JWOStoreManagementPlatform: In cloudscape mode, skipping store check')
            return
        }

        // Login not known yet: don't lock the one-time check, re-run once it lands.
        if (shouldCheckLogin && !isUserLoggedIn) {
            console.log('JWOStoreManagementPlatform: User not logged in yet, deferring store check')
            return
        }

        // Show sheet if no store selected
        const shouldShowSheet = !storeId || storeId === NO_CURRENT_STORE

        if (shouldShowSheet) {
            console.log('JWOStoreManagementPlatform: No store selected, showing store selection sheet')
            setIsSheetOpen(true)
        }

        setHasCheckedInitialStore(true)
    }, [hasCheckedInitialStore, storeId, location.pathname, isUserLoggedIn, shouldCheckLogin, isProd])

    // Navigate to AssociateEntryTool only after selectedStoreId is committed to Redux state
    useEffect(() => {
        if (shouldNavigateToAssociateEntry && selectedStoreId) {
            console.log('JWOStoreManagementPlatform: Navigating to Associate Entry tool')
            setShouldNavigateToAssociateEntry(false)
            const tool = getAssociateEntryTool()
            if (tool) {
                _navigateToTool(tool)
            } else {
                console.warn('JWOStoreManagementPlatform: Associate Entry tool not found in registry')
            }
        }
    }, [shouldNavigateToAssociateEntry, selectedStoreId])

    const handleStoreSelectionComplete = () => {
        console.log('JWOStoreManagementPlatform: Store selection complete, closing sheet')
        setIsSheetOpen(false)
        setShouldNavigateToAssociateEntry(true)
    }

    const handleCloseSheet = () => {
        console.log('JWOStoreManagementPlatform: Store selection sheet closed without selection')
        setIsSheetOpen(false)
    }

    return (
    <StoreSelectionSheet
      open={isSheetOpen}
      onClose={handleCloseSheet}
      onComplete={handleStoreSelectionComplete}
    />
    )
}

export default StoreSelectionSheetController
